import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { saveAs } from 'file-saver';
import { PDFPreview } from './PDFPreview';

interface PDFSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (message?: string, sendToChat?: boolean) => Promise<void>;
  pdfBlob: Blob | null;
  fileName: string;
  patientName: string;
  initialMode?: 'chat' | 'save';
}

export const PDFSendModal: React.FC<PDFSendModalProps> = ({
  isOpen,
  onClose,
  onSend,
  pdfBlob,
  fileName,
  patientName,
  initialMode = 'chat',
}) => {
  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendMode, setSendMode] = useState<'chat' | 'save'>(initialMode);

  useEffect(() => {
    if (!isOpen || !pdfBlob) {
      setPreviewUrl(null);
      setPreviewError(null);
      return;
    }

    try {
      const url = URL.createObjectURL(pdfBlob);
      setPreviewUrl(url);
      setPreviewError(null);
      return () => URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao gerar prévia do PDF:', error);
      setPreviewUrl(null);
      setPreviewError('Não foi possível mostrar a prévia. Ainda é possível salvar o arquivo.');
    }
  }, [isOpen, pdfBlob]);

  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setSendMode(initialMode);
      setActionError(null);
    }
  }, [initialMode, isOpen]);

  const handleSend = async () => {
    if (!pdfBlob) return;
    setSending(true);
    setActionError(null);
    try {
      if (sendMode === 'save') {
        saveAs(pdfBlob, fileName || 'documento.pdf');
      } else {
        await onSend(message.trim() || undefined, true);
      }
      setMessage('');
      setSendMode('chat');
      onClose();
    } catch (error) {
      console.error('Erro ao enviar PDF:', error);
      setActionError(sendMode === 'save' ? 'Não foi possível baixar o PDF.' : 'Não foi possível enviar o PDF pelo chat.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="PDF do paciente"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={sending || !pdfBlob}>
            {sending ? (sendMode === 'save' ? 'Preparando...' : 'Enviando...') : (sendMode === 'save' ? 'Salvar PDF' : 'Enviar PDF')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label className="ns-field">
            <span>Paciente</span>
            <input
              type="text"
              className="ns-input"
              value={patientName}
              disabled
            />
          </label>
        </div>

        <div>
          <label className="ns-field">
            <span>Nome do Arquivo</span>
            <input
              type="text"
              className="ns-input"
              value={fileName}
              disabled
            />
          </label>
        </div>

        <div>
          <label className="ns-field">
              <span>Ação do PDF *</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="sendMode"
                  value="chat"
                  checked={sendMode === 'chat'}
                  onChange={() => setSendMode('chat')}
                />
                <span style={{ fontSize: '14px' }}>Enviar pelo chat (recomendado)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="sendMode"
                  value="save"
                  checked={sendMode === 'save'}
                  onChange={() => setSendMode('save')}
                />
                <span style={{ fontSize: '14px' }}>Baixar PDF no computador</span>
              </label>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '8px' }}>
              {sendMode === 'chat'
                ? 'O PDF será salvo e uma mensagem será enviada no chat do paciente.'
                : 'O PDF será baixado para o seu computador e não será enviado ao paciente.'}
            </p>
            {sendMode === 'save' && <p style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px', fontStyle: 'italic' }}>
              Nota: o arquivo será baixado para o seu computador.
            </p>}
          </label>
        </div>

        {sendMode === 'chat' && (
          <div>
            <label className="ns-field">
              <span>Mensagem Personalizada (Opcional)</span>
              <textarea
                className="ns-textarea"
                rows={3}
                placeholder="Adicione uma mensagem personalizada para o paciente..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>
            <p style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>
              Esta mensagem será enviada junto com o PDF no chat do paciente.
            </p>
          </div>
        )}

        {actionError && <p className="ns-pdf-error" role="alert">{actionError}</p>}

        {previewUrl && (
          <div>
            <label className="ns-pdf-preview-label">
              Preview do PDF
            </label>
            <div className="ns-pdf-preview-frame">
              {pdfBlob && <PDFPreview blob={pdfBlob} />}
            </div>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">Abrir PDF em nova aba</a>
            <p style={{ fontSize: '11px', color: 'var(--page-text-muted)', marginTop: '4px' }}>
              Visualize o PDF antes de {sendMode === 'chat' ? 'enviar' : 'salvar'}.
            </p>
          </div>
        )}
        {previewError && <p className="ns-pdf-error" role="status">{previewError}</p>}
      </div>
    </Modal>
  );
};
